import { mercuryApi } from '$api'
import { AuthModule, PayrollSystem } from '$constants'
import {
  Calendar,
  EarningsRate,
  PayRun,
  PayrollEmployee,
  transformCalendarOnGet,
  transformPayRunOnGet,
  transformPayrollEmployeeOnGet,
  SuperFund,
  ThirdPartyConnectionStatus,
} from '$models'
import { getActiveCompanySettings } from '$utils'
import { Dayjs } from 'dayjs'
import orderBy from 'lodash/orderBy'

/**
 * Get Payroll Employees
 * @param payrollSystem to get the payroll employees from
 * @returns Promise<PayrollEmployee[]>
 */
export const getPayrollEmployees = async (
  payrollSystem: PayrollSystem
): Promise<PayrollEmployee[]> => {
  const payrollEmployees = await mercuryApi.get<PayrollEmployee[]>(
    `/payroll/employees?system=${payrollSystem}`
  )

  return payrollEmployees.map((payrollEmployee) =>
    transformPayrollEmployeeOnGet(payrollSystem, payrollEmployee)
  )
}

/**
 * Get a Payroll Employee
 * @param payrollSystem to get the payroll employee from
 * @returns Promise<PayrollEmployee>
 */
export const getPayrollEmployee = async (
  payrollSystem: PayrollSystem,
  payrollEmployeeId: string
): Promise<PayrollEmployee> => {
  const payrollEmployee = await mercuryApi.get<PayrollEmployee>(
    `/payroll/employees/${payrollEmployeeId}?system=${payrollSystem}`
  )

  return transformPayrollEmployeeOnGet(payrollSystem, payrollEmployee)
}

/**
 * Get Payroll Calendars
 * @param payrollSystem to get the calendars from
 * @returns Promise<Calendar[]>
 */
export const getCalendars = async (
  payrollSystem: PayrollSystem
): Promise<Calendar[]> => {
  const { hasManagerPermission } = getActiveCompanySettings()

  if (!hasManagerPermission) return []

  const calendars = await mercuryApi.get<Calendar[]>(
    `/payroll/calendars?system=${payrollSystem}`
  )

  return calendars.map(transformCalendarOnGet)
}

/**
 * Get Payroll Earnings Rates
 * @param payrollSystem to get the earnings rates from
 * @returns Promise<EarningsRate[]>
 */
export const getEarningsRates = async (
  payrollSystem: PayrollSystem
): Promise<EarningsRate[]> => {
  const { hasManagerPermission } = getActiveCompanySettings()

  if (payrollSystem !== PayrollSystem.XeroAu || !hasManagerPermission) return []

  const earningsRates = await mercuryApi.get<EarningsRate[]>(
    `/payroll/pay-items/earnings-rates?system=${payrollSystem}`
  )

  return earningsRates
}

/**
 * Get SuperFunds from a Payroll system
 * @param payrollSystem to get the super funds from
 * @returns Promise<SuperFund[]>
 */
export const getPayrollSuperFunds = async (
  payrollSystem: PayrollSystem
): Promise<SuperFund[]> => {
  if (payrollSystem !== PayrollSystem.XeroAu) return []

  // get super funds from external payroll system
  const superFunds = await mercuryApi.get<SuperFund[]>(
    `/payroll/third-party-super-funds?system=${payrollSystem}`
  )

  return superFunds
}

/**
 * Get Sper Funds from the Loaded databases
 * @returns Promise<SuperFund[]>
 */
export const getSuperFunds = async (): Promise<SuperFund[]> => {
  const superFunds = await mercuryApi.get<SuperFund[]>(`/payroll/super-funds`)
  return superFunds
}

/**
 * Get Payroll PayRuns
 * @param payrollSystem to get the payRuns from
 * @returns Promise<PayRun[]>
 */
export const getPayRuns = async (
  payrollSystem: PayrollSystem
): Promise<PayRun[]> => {
  const payRuns = await mercuryApi.get<PayRun[]>(
    `/payroll/pay-runs?system=${payrollSystem}`
  )

  return orderBy(payRuns.map(transformPayRunOnGet), 'periodStartDate', 'desc')
}

/**
 * Add PayRun
 * @param payrollSystem to add the payRuns to
 * @param payRun - payRun to add
 * @returns Promise<PayRun>
 */
export const addPayRun = async (
  payrollSystem: PayrollSystem,
  payRun: { paymentDate: Dayjs; calendarId: string }
): Promise<PayRun> => {
  const payrun = await mercuryApi.add(
    `/payroll/pay-runs?system=${payrollSystem}`,
    {
      ...payRun,
      paymentDate: payRun.paymentDate.startOf('day').utc().format(),
    }
  )

  return transformPayRunOnGet(payrun)
}

/**
 * Check Third Party Connection Status
 * @param authModule AuthModule to check
 * @returns Promise<boolean>
 */
export const getThirdPartyConnectionStatus = async (
  authModule: AuthModule
): Promise<ThirdPartyConnectionStatus> => {
  const thirdPartyConnectionStatus =
    await mercuryApi.get<ThirdPartyConnectionStatus>(
      `/connections/xero/status?module=${authModule}`
    )

  return thirdPartyConnectionStatus
}
